import { defaultCtx } from "~/lib/AjaxCtx"
import { notify, leaveBreadcrumb } from "~/lib/bugsnag"

const DEFAULT_BLOCK_INSTANCE = {
    items: [],
    status: "ready", // ready | refreshing | error
    lastUpdate: new Date(0),
}

// Blocks won't be updated unless this time has passed from their last update.
// TODO: Recover this feature once all elements in the store are normalized https://github.com/okn/prometeo/issues/3015
const REFRESH_THRESHOLD = 0 // 0 ms, it used to be 5 min

export const getters = {
    // Returns a space's blocks as an array.
    blocks: state => spaceId => {
        const space = state.spaces[spaceId]
        const blockIds = space?.description?.blockIds ?? []

        const blocks = []
        for (const blockId of blockIds) {
            const block = state.blocks[blockId]
            if (!block) {
                notify(`Coudn't find block: ${spaceId}/${blockId}`)
            }
            blocks.push(block)
        }
        return blocks
    },
}

export default {
    namespaced: true,

    state: {
        links: [],
        spaces: {}, // spaceId  => SpaceSate
        blocks: {}, // blockId  => BlockState
    },

    getters,

    mutations: {
        setLinks(state, links) {
            state.links = links
        },

        setSpacesFromDescriptions(state, spaceDescriptions) {
            for (let description of spaceDescriptions) {
                if (description.spaceId in state.spaces) {
                    state.spaces[description.spaceId].description = description
                } else {
                    state.spaces[description.spaceId] = {
                        description,
                    }
                }
            }
        },

        setBlocksFromDescriptions(state, blockDescriptions) {
            for (const description of blockDescriptions) {
                if (description.blockId in state.blocks) {
                    state.blocks[description.blockId].description = description
                    // TODO maybe we have to clean something here
                } else {
                    state.blocks[description.blockId] = Object.assign(
                        {},
                        DEFAULT_BLOCK_INSTANCE,
                        { description }
                    )
                }
            }
        },

        setBlockStatus(state, { blockId, status }) {
            const block = state.blocks[blockId]
            if (!block) {
                leaveBreadcrumb(state.blocks)
                throw new Error(
                    `Block ${blockId} was not found when trying to set status ${status}`
                )
            }
            block.status = status
        },

        updateBlock(state, { blockId, block }) {
            const oldBlock = state.blocks[blockId]
            state.blocks[blockId] = Object.assign({}, oldBlock, block)
        },

        updateItem(state, { item, blockId }) {
            const { key } = item._meta
            const block = state.blocks[blockId]
            const oldItem = block.items.find(i => i.value[key] === item[key])
            Object.assign(oldItem.value, item)
        },

        clearModule(state) {
            state.links = []
            state.spaces = {}
            state.blocks = {}
        },
    },

    actions: {
        async refreshStructure({ state, dispatch }) {
            if (state.links.length) {
                defaultCtx.fetchData("/structure").then(structure => {
                    dispatch("saveStructure", structure)
                })
                return
            }
            const structure = await defaultCtx.fetchData("/structure")
            dispatch("saveStructure", structure)
        },

        saveStructure({ commit }, structure) {
            const { links, spaces, blocks, lists } = structure
            commit("setLinks", links)
            commit("setBlocksFromDescriptions", blocks)
            commit("setSpacesFromDescriptions", spaces)
            for (const description of lists) {
                commit("lists/setDescription", description, { root: true })
            }
        },

        refreshBlock(
            { state, commit, rootGetters },
            { blockId, force = false }
        ) {
            const block = state.blocks[blockId]
            if (!force && new Date() - block.lastUpdate < REFRESH_THRESHOLD)
                return

            const source = block.description.itemsSource
            if (!source) {
                throw new Error(
                    `itemsSource of ${block.description.blockId} is not defined`
                )
            }
            switch (source.type) {
                case "url":
                    commit("setBlockStatus", { blockId, status: "refreshing" })
                    defaultCtx.fetchData(source.url).then(
                        items => {
                            commit("updateBlock", {
                                blockId,
                                block: {
                                    status: "ready",
                                    items: items.map(item => ({
                                        value: item,
                                        type: getItemType(
                                            item,
                                            source.itemsType
                                        ),
                                    })),
                                    lastUpdate: new Date(),
                                },
                            })
                        },
                        () => {
                            if (!rootGetters.currentUser) return // Trying to save the block status is worthless when the user is logged out
                            commit("setBlockStatus", {
                                blockId,
                                status: "error",
                            })
                        }
                    )
                    break

                case "static":
                    commit("updateBlock", {
                        blockId,
                        block: {
                            items: source.items,
                            lastUpdate: new Date(),
                        },
                    })
                    break

                default:
                    throw new Error(`Unknown item source type ${source.type}`)
            }
        },
    },
}

export function getItemType(item, type) {
    if (typeof type == "string") return type
    const { map, prop } = type
    if (!(prop in item)) throw new Error(`Couldn't find '${prop}' in ${item}.`)
    const value = item[prop]
    if (!(value in map)) throw new Error(`No type defined for ${value}.`)
    return map[value]
}
